import { useQuery } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';
import { useMemo, useState } from 'react';

import { Btn } from '../../components/Btn';
import { EmptyState } from '../../components/EmptyState';
import { DataTable } from '../../components/Table';
import { listBranches } from '../../data/fetchers';
import { canEditSystemConfig } from '../../lib/permissions';
import { useSession } from '../../stores/session';
import type { Branch } from '../../types/domain';
import { BranchFormModal } from './BranchFormModal';
import { SettingsHeader } from './SettingsHeader';

export function BranchesView() {
  const user = useSession((s) => s.user);
  const canEdit = user ? canEditSystemConfig(user) : false;
  const { data: branches = [] } = useQuery({
    queryKey: ['branches'],
    queryFn: listBranches,
  });
  const [editing, setEditing] = useState<Branch | null>(null);
  const [modalOpen, setModalOpen] = useState(false);

  const openAdd = () => {
    setEditing(null);
    setModalOpen(true);
  };
  const openEdit = (b: Branch) => {
    setEditing(b);
    setModalOpen(true);
  };

  const columns = useMemo<ColumnDef<Branch, unknown>[]>(
    () => [
      { accessorKey: 'name', header: 'ชื่อสาขา' },
      { accessorKey: 'short', header: 'ชื่อย่อ' },
      {
        accessorKey: 'code',
        header: 'รหัสสาขา',
        cell: (c) => c.row.original.code || 'ยังไม่ตั้งรหัส',
      },
      {
        id: 'go',
        header: '',
        cell: (c) => (
          <button
            aria-label={`แก้ไขสาขา ${c.row.original.name}`}
            className="font-bold text-primary text-sm-label hover:underline"
            onClick={() => openEdit(c.row.original)}
          >
            แก้ไข
          </button>
        ),
      },
    ],
    [],
  );

  if (!canEdit) {
    return (
      <div className="bo-fade">
        <SettingsHeader
          title="ร้านค้าและสาขา"
          subtitle="ดูแลข้อมูลสาขาที่ใช้ร่วมกันในรายงาน สต๊อก และเครื่อง POS"
          status={{ label: 'คุณไม่มีสิทธิ์แก้ไข', tone: 'neutral' }}
        />
        <div className="bo-card">
          <EmptyState
            title="เฉพาะเจ้าของ"
            sub="การจัดการสาขาทำได้โดยเจ้าของหรือผู้ที่ได้รับสิทธิ์ตั้งค่าระบบ"
          />
        </div>
      </div>
    );
  }

  return (
    <div className="bo-fade">
      <SettingsHeader
        title="ร้านค้าและสาขา"
        subtitle="ดูแลข้อมูลสาขาที่ใช้ร่วมกันในรายงาน สต๊อก และเครื่อง POS"
        status={{
          label:
            branches.length > 0
              ? `${branches.length} สาขาในระบบ`
              : 'ยังไม่มีสาขา',
          tone: branches.length > 0 ? 'ok' : 'warn',
        }}
        actions={
          <Btn kind="primary" onClick={openAdd}>
            + เพิ่มสาขา
          </Btn>
        }
      />
      <DataTable columns={columns} data={branches} />
      <BranchFormModal
        open={modalOpen}
        branch={editing}
        onClose={() => setModalOpen(false)}
      />
    </div>
  );
}
